import type { ReactNode } from "react";
import { PERFIS_DO_LAB, ROTULO_DA_FAMILIA, ROTULO_DA_FAMILIA_DE_MATERIAL } from "@bookdental/schemas";
import type { OpcoesDaBase } from "@bookdental/api";
import { SeletorDeChips } from "../SeletorDeChips";

export type { OpcoesDaBase };

export const entradaCls =
  "h-9 w-full rounded-md border border-admin-200 bg-white px-3 text-sm text-admin-900 outline-none focus:border-admin-700";

export function Campo({ rotulo, dica, children }: { rotulo: string; dica?: string; children: ReactNode }) {
  return (
    <div className="space-y-1.5">
      <p className="text-[12.5px] font-semibold text-admin-900">{rotulo}</p>
      {children}
      {dica && <p className="text-[11.5px] text-admin-700/70">{dica}</p>}
    </div>
  );
}

/** Texto com sugestões das opções existentes; o que não existe é criado ao salvar. */
export function TextoComSugestoes({
  id,
  valor,
  onChange,
  sugestoes,
  placeholder,
}: {
  id: string;
  valor: string;
  onChange: (v: string) => void;
  sugestoes: string[];
  placeholder?: string;
}) {
  return (
    <>
      <input list={id} value={valor} onChange={(e) => onChange(e.target.value)} placeholder={placeholder} className={entradaCls} />
      <datalist id={id}>
        {sugestoes.map((s) => (
          <option key={s} value={s} />
        ))}
      </datalist>
    </>
  );
}

export const opcoesDeQuimica = (o: OpcoesDaBase) =>
  o.quimicas.map((q) => ({
    id: q.id,
    rotulo: q.label,
    grupo: ROTULO_DA_FAMILIA[q.familia ?? ""] ?? "Sem família",
    apagada: q.status !== "PUBLICADO",
  }));

export const opcoesDeMaterial = (o: OpcoesDaBase) =>
  o.materiais.map((m) => ({
    id: m.id,
    rotulo: m.nome,
    grupo: ROTULO_DA_FAMILIA_DE_MATERIAL[m.familia ?? ""] ?? "Sem família",
    apagada: m.status !== "PUBLICADO",
  }));

export function SeletorDeQuimica({ opcoes, valor, onChange }: { opcoes: OpcoesDaBase; valor: string; onChange: (id: string) => void }) {
  const grupos = [...new Set(opcoesDeQuimica(opcoes).map((q) => q.grupo))];
  return (
    <select value={valor} onChange={(e) => onChange(e.target.value)} className={entradaCls}>
      <option value="">Escolha a química</option>
      {grupos.map((g) => (
        <optgroup key={g} label={g}>
          {opcoesDeQuimica(opcoes)
            .filter((q) => q.grupo === g)
            .map((q) => (
              <option key={q.id} value={q.id}>
                {q.rotulo}
              </option>
            ))}
        </optgroup>
      ))}
    </select>
  );
}

const PERFIS = [...PERFIS_DO_LAB.map((p) => ({ id: p.codigo, rotulo: p.rotulo })), { id: "GERAL", rotulo: "Todo laboratório" }];

export function CampoPerfis({ valor, onChange }: { valor: string[]; onChange: (v: string[]) => void }) {
  return (
    <Campo rotulo="Perfis que sugerem" dica="No onboarding, o item já vem marcado para o laboratório com algum desses perfis.">
      <SeletorDeChips opcoes={PERFIS} valor={valor} onChange={onChange} vazio="Nenhum — aparece desmarcado" />
    </Campo>
  );
}
